var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import "altcha";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from "react";
import { useIntl } from "react-intl";
import { useOkapiKy } from "@folio/stripes/core";
import { isNetworkError } from "../utils/isNetworkError";
/** Tag of the altcha web component (registered by the side-effect import above). */
var ALTCHA_ELEMENT = "altcha-widget";
/** HTTP header carrying the solved captcha (base64 widget payload) on a protected request (DB-876). */
export var ALTCHA_HEADER = "X-Altcha";
/** Widget text keys mapped to the translation ids that hold their Czech wording. */
export var ALTCHA_STRING_KEYS = {
    ariaLinkLabel: "stripes-core.altcha.ariaLinkLabel",
    error: "stripes-core.altcha.error",
    expired: "stripes-core.altcha.expired",
    footer: "stripes-core.altcha.footer",
    label: "stripes-core.altcha.label",
    verificationRequired: "stripes-core.altcha.verificationRequired",
    verified: "stripes-core.altcha.verified",
    verifying: "stripes-core.altcha.verifying",
    waitAlert: "stripes-core.altcha.waitAlert",
};
/**
 * Error text shown instead of {@link ALTCHA_STRING_KEYS.error} when the challenge request never
 * reached the server (RCK-1657), so a client network problem does not look like a failed check.
 */
export var ALTCHA_NETWORK_ERROR_KEY = "stripes-core.altcha.networkError";
var NO_CACHE_HEADERS = {
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
};
/**
 * Self-hosted ALTCHA proof-of-work captcha for the anonymous forms of the login side (DB-876).
 * The challenge is fetched through the okapi ky client, so it carries the same tenant headers as
 * the other anonymous calls of the form; the solved payload goes to the protected request in the
 * {@link ALTCHA_HEADER} header.
 */
export var AltchaWidget = forwardRef(function (_a, ref) {
    var challengePath = _a.challengePath, onPayload = _a.onPayload;
    var elementRef = useRef(null);
    var onPayloadRef = useRef(onPayload);
    onPayloadRef.current = onPayload;
    // useOkapiKy() returns a new client on every render; the widget keeps one stable fetcher.
    var ky = useOkapiKy();
    var kyRef = useRef(ky);
    kyRef.current = ky;
    var formatMessage = useIntl().formatMessage;
    var _b = useState(false), isServerUnreachable = _b[0], setIsServerUnreachable = _b[1];
    var strings = useMemo(function () {
        return JSON.stringify(Object.fromEntries(Object.entries(ALTCHA_STRING_KEYS).map(function (_a) {
            var key = _a[0], id = _a[1];
            return [
                key,
                formatMessage({
                    id: key === "error" && isServerUnreachable
                        ? ALTCHA_NETWORK_ERROR_KEY
                        : id,
                }),
            ];
        })));
    }, [formatMessage, isServerUnreachable]);
    /**
     * Loads the challenge; a rejection still fails the widget (it switches to its error state),
     * only the error text depends on whether the server was reachable at all.
     */
    var fetchChallenge = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setIsServerUnreachable(false);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, kyRef.current.get(challengePath, {
                            headers: NO_CACHE_HEADERS,
                        })];
                case 2: return [2 /*return*/, _a.sent()];
                case 3:
                    error_1 = _a.sent();
                    setIsServerUnreachable(isNetworkError(error_1));
                    throw error_1;
                case 4: return [2 /*return*/];
            }
        });
    }); }, [challengePath]);
    useEffect(function () {
        if (elementRef.current) {
            elementRef.current.customfetch = fetchChallenge;
        }
    }, [fetchChallenge]);
    useEffect(function () {
        var element = elementRef.current;
        if (!element) {
            return undefined;
        }
        var handleStateChange = function (event) {
            var _a;
            var _b = (_a = event.detail) !== null && _a !== void 0 ? _a : {}, state = _b.state, payload = _b.payload;
            onPayloadRef.current(state === "verified" && payload ? payload : null);
        };
        element.addEventListener("statechange", handleStateChange);
        return function () {
            return element.removeEventListener("statechange", handleStateChange);
        };
    }, []);
    useImperativeHandle(ref, function () { return ({
        reset: function () {
            var element = elementRef.current;
            if (typeof (element === null || element === void 0 ? void 0 : element.reset) === "function") {
                element.reset();
            }
            onPayloadRef.current(null);
        },
    }); }, []);
    return (
    // createElement with the tag name instead of JSX: the build must not depend on the altcha
    // package's ambient JSX.IntrinsicElements augmentation being picked up by the type checker.
    React.createElement(ALTCHA_ELEMENT, {
        ref: elementRef,
        challengeurl: challengePath,
        strings: strings,
    }));
});
AltchaWidget.displayName = "AltchaWidget";
